import React, { createContext, SetStateAction, useCallback, useMemo, useRef, useState } from 'react'
import { CommonContextProps, LazyParams, ToastProps } from '../../../types/common'
import { Toast } from 'primereact/toast'
import { ChildContainerProps } from '../../../types/types'
import { Button } from 'primereact/button'
import { DataTableStateEvent } from 'primereact/datatable'
import { BlockUI } from 'primereact/blockui'
import { random } from 'lodash'
import { download, getAxios } from '../../../utils/axiosUtils'
import { CustomerConsultation } from '../../../types/customerConsultation'
import { useFormik } from 'formik'
import { object, string } from 'yup'
import ExcelDownAnimation from '../ExcelDownAnimation'
import {useTranslation} from "react-i18next";
import { tokens } from '../../../locales/tokens';

export const CommonContext = createContext({} as CommonContextProps)
const initialValues: CustomerConsultation = {
    cnslnSeq: undefined,
    cnslnDt: '',
    cnslnTypCd: 'COMMON',
    spId: '',
    nonMemYn: 'N',
    inqTypCd: '',
    inqDtlTypCd: '',
    fnlInqTypCd: '',
    fnlDtlTypCd: '',
    cnslnTxt: '',
    rqstsCd: '',
    sbjctRspnsCd: '',
    fnlSbjctRspnsCd: '',
    cnslnrId: '',
    cnslnrAuthGrpId: undefined,
    tel: '',
    prcsnrId: '',
    prcsnTxt: '',
    prcsnSttCd: '',
    returnSttCd: '',
    prcsnDt: '',
    prcsnAuthGrpId: undefined,
    mpngId: '',
    returnExchangeGoods: [],
    files: [],
    isPossibleClaimProcess: '',
}
export const CommonProvider = (props: ChildContainerProps) => {
    const { t, i18n } = useTranslation();
    const [blocked, setBlocked] = useState<boolean>(false)
    const [isExcel, setIsExcel] = useState<boolean>(false)
    const [isReFresh, setIsReFresh] = useState<boolean>(false)
    const [lazyInput, setLazyInput] = useState<NodeJS.Timeout>()
    const alert = useRef<Toast>(null)
    const toast = useRef<Toast>(null)

    const $alert = useCallback((props: ToastProps): Promise<boolean> => {
        return new Promise(function (resolve) {
            const isConfirm = props?.isConfirm || false
            const icon = props?.icon || 'info'
            let iconImage = 'pi-info-circle'

            // pi-info-circle info
            // pi-check-circle success
            // pi-exclamation-triangle warn
            // pi-times-circle error
            switch (icon) {
                case 'success':
                    iconImage = 'pi-check-circle'
                    break
                case 'warn':
                    iconImage = 'pi-exclamation-triangle'
                    break
                case 'error':
                    iconImage = 'pi-times-circle'
                    break
            }

            const clear = (submit: boolean) => {
                if (alert && alert.current) {
                    alert.current.clear()
                    setBlocked(false)
                }
                return resolve(submit)
            }

            const message = {
                id: random(0, 9000).toString(),
                severity: props?.icon || 'info',
                closable: false,
                sticky: true,
                style: {
                    zIndex: 3000,
                },
                content: (
                    <div className="flex flex-column rdms-toast" style={{ flex: '1' }}>
                        <div className="align-items-start text-left">
                            <div className="font-bold text-xl mt-1 justify-content-start">{props?.title || ''}</div>
                        </div>
                        <div className="text-left my-5">
                            <div className="flex min-w-0">
                                <i className={`pi ${iconImage}`} style={{ fontSize: '3rem' }}></i>
                                <span className="ml-4" style={{ wordBreak: 'break-all', whiteSpace: 'pre-line' }}>
                                    {props?.text || ''}
                                </span>
                            </div>
                        </div>

                        {isConfirm ? (
                            <div className="justify-content-center flex gap-2">
                                <Button onClick={() => clear(false)} type="button" label={t(tokens.common.cancellation)} className="p-button-sm p-button-warning w-6rem" />
                                <Button onClick={() => clear(true)} type="button" label={t(tokens.common.check)} className="p-button-sm p-button-success w-6rem" />
                            </div>
                        ) : (
                            <div className="justify-content-center flex gap-2">
                                <Button onClick={() => clear(true)} type="button" label={t(tokens.common.check)} className="p-button-secondary w-6rem" />
                            </div>
                        )}
                    </div>
                ),
            }

            setBlocked(true)
            setTimeout(() => {
                if (alert && alert.current) {
                    alert.current.show(message)
                }
            }, 300)
        })
    }, [])

    const $toast = useCallback((props: ToastProps) => {
        if (toast && toast.current) {
            toast.current.show({
                severity: props.icon,
                summary: props.title,
                detail: props.text,
            })
        }
    }, [])

    const $http = useCallback(() => {
        return getAxios(process.env.REACT_APP_API_BASE_URL, $alert, t, setBlocked, setIsExcel)
    }, [$alert, t])

    const $file = useCallback(() => {
        return getAxios(process.env.REACT_APP_FILE_API_URL, $alert, t )
    }, [$alert, t])

    const $batch = useCallback(() => {
        return getAxios(process.env.REACT_APP_BATCH_API_BASE_URL, $alert, t, setBlocked, setIsExcel)
    }, [$alert, t])

    const $onPage = useCallback((e: DataTableStateEvent, params: any, setParams: SetStateAction<any>) => {
        const { page, first } = e
        const returnParams = {
            ...params,
            first,
            page: (page ? page : 0) + 1,
            pageSize: e.rows,
        }

        setParams(returnParams)

        return returnParams
    }, [])

    const $onSort = useCallback((e: LazyParams, params: any, setParams: SetStateAction<any>) => {
        let sort

        if (e.sortOrder === 1 || e.sortOrder === -1) {
            sort = `${e.sortField} ${e.sortOrder === 1 ? 'asc' : 'desc'}`
        } else {
            sort = ''
        }

        const returnParams = {
            ...params,
            sort,
            sortField: e.sortField,
            sortOrder: e.sortOrder,
        }

        setParams(returnParams)

        return returnParams
    }, [])

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const $lazyInput = (action: Function, delayTime: number = 500) => {
        if (lazyInput) clearTimeout(lazyInput)
        setLazyInput(
            setTimeout(function () {
                action()
            }, delayTime),
        )
    }

    const $codes = useCallback(
        async (codeGroupIds: any, isAll: boolean = false, isSelect?: boolean) => {
            const data: Array<any> = await $http().post('/code', { codeGroupIds, isAll })

            let language = i18n.resolvedLanguage;

            data.forEach((list) => {
                list.forEach((l: any) => {
                    l.codeNm = language === 'ko' ? l.codeNm : l.japanCodeNm
                    l.displayNm = language === 'ko'? l.codeNm : l.japanDisplayNm
                })
            })

            if (isAll && isSelect) {
                data.forEach((list) => {
                    list.forEach((l: any) => {
                        const codeNm = l.codeId === '' ? t(tokens.common.select)
                            : language === 'ko' ? l.codeNm : l.japanCodeNm
                        l.codeNm = codeNm
                        l.displayNm = codeNm
                    })
                })

                return data
            }

            return data
        },
        [$http, i18n.resolvedLanguage],
    )

    const $down = useCallback((uri: string, fileNm: string) => {
        download(process.env.REACT_APP_FILE_DOWNLOAD_URL || '', uri, fileNm)
    }, [])

    const consultationFormik = useFormik({
        initialValues,
        validateOnMount: false,
        validateOnChange: false,
        validationSchema: object({
            spId: string().when('nonMemYn', {
                is: (nonMemYn: string) => nonMemYn === 'N',
                then: (schema) => schema.required(t(tokens.common.customer_selection_prompt)),
            }),
            tel: string().when('nonMemYn', {
                is: (nonMemYn: string) => nonMemYn === 'Y',
                then: (schema) => schema.required(t(tokens.common.contact_information_required)),
            }),
            inqTypCd: string().required(t(tokens.common.counsel_type_required)),
            cnslnTxt: string().required(t(tokens.common.counsel_content_required)),
            prcsnSttCd: string().when('cnslnTypCd', {
                is: (cnslnTypCd: string) => cnslnTypCd === 'COMMON',
                then: (schema) => schema.required(t(tokens.common.treatment_status_required)),
            }),
            inqDtlTypCd: string().when('cnslnTypCd', {
                is: (cnslnTypCd: string) => cnslnTypCd === 'CLAIM',
                then: (schema) => schema.required(t(tokens.common.counsel_type_detail_required)),
            }),
            sbjctRspnsCd: string().when('cnslnTypCd', {
                is: (cnslnTypCd: string) => cnslnTypCd === 'CLAIM',
                then: (schema) => schema.required(t(tokens.common.your_fault_mandatory)),
            }),
            prcsnAuthGrpId: string().when('cnslnTypCd', {
                is: (cnslnTypCd: string) => cnslnTypCd === 'CLAIM',
                then: (schema) => schema.required(t(tokens.common.authority_group_required)),
            }),
        }),
        onSubmit: async (data) => {
            if (data.nonMemYn === 'N') {
                // console.log('spId : ', data.spId)
                if (!data.spId) {
                    // console.log('asdf : ', data.spId)
                    consultationFormik.setErrors({ spId: t(tokens.common.customer_selection_prompt) })
                    return false
                }
            } else {
                if (!data.tel) {
                    consultationFormik.setErrors({ tel: t(tokens.common.enter_phone_number_please) })
                    return false
                }
            }
            const result = await $http().post(`customer-list/consultation`, data)

            if (result) {
                $toast({
                    title: t(tokens.common.success),
                    text: t(tokens.common.save_completed),
                    icon: 'success',
                })
                setIsReFresh(true)
                consultationFormik.resetForm()
                // await consultationFormik.setValues(initialValues)
                // setCommonSearchInitValue('')
            }
        },
    })

    const reFreshFlag = useCallback(() => {
        return isReFresh
    }, [isReFresh])

    const setReFreshFlag = (flag: boolean) => {
        setIsReFresh(flag)
    }

    const value = useMemo(
        () => ({
            $alert,
            $http,
            $file,
            $batch,
            $onPage,
            $onSort,
            $lazyInput,
            $codes,
            $toast,
            $down,
            consultationFormik,
            reFreshFlag,
            setReFreshFlag,
        }),
        [$alert, $http, $file, $batch, $onPage, $onSort, $lazyInput, $codes, $toast, $down, consultationFormik, reFreshFlag, setReFreshFlag],
    )

    return (
        <CommonContext.Provider value={value}>
            <BlockUI blocked={blocked} autoZIndex={false} style={{ zIndex: '2999' }} fullScreen />
            {blocked && isExcel && <ExcelDownAnimation />}
            <Toast ref={alert} position="center" className="opacity-100" baseZIndex={3000} />
            <Toast ref={toast} position="top-right" className="opacity-100" baseZIndex={3000} />
            {props.children}
        </CommonContext.Provider>
    )
}
